import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSite } from "@/context/SiteContext";
import api from "@/lib/api";
import QRScannerModal from "@/components/QRScannerModal";
import {
  Trash2, FileWarning, Activity, ScanLine, ChevronRight, CheckCircle2,
  RefreshCw, ListChecks, ArrowRight, Gauge,
} from "lucide-react";

const SUPERVISOR_PLUS = ["owner", "manager", "supervisor"];

// Fetch the three critical-task buckets, scoped to the caller's role.
// Each fetch is isolated so a single 403 never blanks the whole screen.
function useCriticalCounts(role) {
  const { activeSiteId } = useSite();
  const [state, setState] = useState({ foodExpiry: 0, dailyTasks: 0, deviations: 0, ccpTask: 0, ccpCapture: 0, ccpSignoff: 0 });
  const [loading, setLoading] = useState(true);

  const isStaff = role === "staff";
  const canReview = SUPERVISOR_PLUS.includes(role);

  const load = useCallback(async () => {
    setLoading(true);
    const next = { foodExpiry: 0, dailyTasks: 0, deviations: 0, ccpTask: 0, ccpCapture: 0, ccpSignoff: 0 };

    // 1) Food expiry — items that must be disposed now (flagged + expired).
    try {
      const { data } = await api.get("/food-prep/disposal-list");
      next.foodExpiry = (data.flagged || []).length + (data.expired || []).length;
    } catch { /* module may be off for this tenant */ }

    // 3) Daily tasks — hygiene + cleanliness records awaiting the caller's action.
    try {
      const qs = (tpl) => [
        isStaff ? null : `/checks?pending_verification=true&template_code=${tpl}&days=30`,
        `/checks?awaiting_rectification=true&template_code=${tpl}&days=60`,
        isStaff ? null : `/checks?awaiting_approval=true&template_code=${tpl}&days=60`,
      ].filter(Boolean);
      const urls = [...qs("DAILY_HYGIENE"), ...qs("CLEAN_FACILITY")];
      const all = await Promise.all(urls.map((u) => api.get(u)));
      const rectifCount = all.reduce((s, r) => s + (r.data.checks || []).length, 0);

      // Also count facilities whose daily log for TODAY hasn't been captured yet
      // (visible on Daily Tracking as "Not yet today"). This is what actually
      // needs the user's attention today, in addition to any rectification / sign-off tail.
      const [hSum, cSum] = await Promise.all([
        api.get("/checks/facility-summary?template_code=DAILY_HYGIENE&days=1"),
        api.get("/checks/facility-summary?template_code=CLEAN_FACILITY&days=1"),
      ]);
      const todayMissing =
        (hSum.data.facilities || []).filter((f) => !f.today_logged).length +
        (cSum.data.facilities || []).filter((f) => !f.today_logged).length;

      next.dailyTasks = rectifCount + todayMissing;
    } catch { /* ignore */ }

    // 2) Deviations — rework (all roles) + reviewer-actionable (supervisor+).
    try {
      const { data } = await api.get("/ccp-task-counts");
      next.deviations = (data.rework || 0) + (canReview ? (data.deviations_actionable || 0) : 0);
    } catch { /* ignore */ }

    // 4) CCP capture (staff) / capture + sign-off (reviewers).
    //    Reviewers get both counters combined into the tile — they're
    //    accountable for chasing staff to log AND for signing off.
    try {
      const { data } = await api.get("/ccp-capture-counts");
      next.ccpCapture = data.pending_capture || 0;
      next.ccpSignoff = data.pending_signoff || 0;
      next.ccpTask = isStaff ? next.ccpCapture : (next.ccpCapture + next.ccpSignoff);
    } catch { /* ignore */ }

    setState(next);
    setLoading(false);
  }, [isStaff, canReview]);

  useEffect(() => { load(); }, [load, activeSiteId]);

  return { ...state, loading, refresh: load };
}

function TaskTile({ priority, icon: Icon, tone, title, subtitle, count, actions }) {
  const done = count === 0;
  return (
    <div
      className="vb-card p-0 overflow-hidden"
      style={{ borderLeft: `5px solid ${done ? "var(--vb-border, #d8cfc4)" : tone}` }}
      data-testid={`critical-tile-${priority}`}
    >
      <div className="flex items-start gap-4 p-5">
        <div
          className="shrink-0 flex items-center justify-center rounded-xl"
          style={{ width: 52, height: 52, background: done ? "rgba(0,0,0,0.05)" : `${tone}1a`, color: done ? "var(--vb-text-2)" : tone }}
        >
          <Icon size={24} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-[10px] uppercase tracking-widest opacity-50 font-semibold">Priority {priority}</span>
            {done ? (
              <span className="vb-pill ok text-[10px]" data-testid={`critical-clear-${priority}`}><CheckCircle2 size={10} /> All clear</span>
            ) : (
              <span
                className="text-xs rounded-full px-2 py-0.5 font-bold"
                style={{ background: tone, color: "#fff", minWidth: 22, textAlign: "center" }}
                data-testid={`critical-count-${priority}`}
              >
                {count}
              </span>
            )}
          </div>
          <h3 className="vb-heading text-lg m-0 leading-tight" style={{ color: "var(--vb-text-2)" }}>{title}</h3>
          <p className="text-sm opacity-70 m-0 mt-0.5">{subtitle}</p>
          <div className="flex gap-2 flex-wrap mt-3">
            {actions.map((a) => (
              <button
                key={a.label}
                className={`vb-btn ${a.ghost ? "ghost" : ""} text-sm`}
                onClick={a.onClick}
                data-testid={a.testid}
                style={!a.ghost && !done ? { background: tone, borderColor: tone, color: "#fff" } : undefined}
              >
                {a.icon && <a.icon size={14} />} {a.label}
                {!a.ghost && <ArrowRight size={14} />}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CriticalTasks() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [scanOpen, setScanOpen] = useState(false);
  const { foodExpiry, dailyTasks, deviations, ccpTask, ccpCapture, ccpSignoff, loading, refresh } = useCriticalCounts(user?.role);

  const total = foodExpiry + dailyTasks + deviations + ccpTask;
  const isStaff = user?.role === "staff";

  const greeting = useMemo(() => {
    const h = new Date().getHours();
    return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
  }, []);

  const devTarget = isStaff ? "/app/log" : "/app/deviations";
  const ccpTarget = isStaff ? "/app/log" : "/app/ccps";

  return (
    <div className="max-w-3xl" data-testid="critical-tasks-page">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-1">
        <div>
          <div className="flex items-center gap-2">
            <ListChecks size={22} style={{ color: "var(--vb-accent)" }} />
            <h1 className="vb-heading text-2xl m-0" style={{ color: "var(--vb-text-2)" }}>Critical Tasks</h1>
          </div>
          <p className="text-sm opacity-70 m-0 mt-1">
            {greeting}, {user?.name?.split(" ")[0] || "there"} — your priority actions for today.
          </p>
        </div>
        <button className="vb-btn ghost text-sm" onClick={refresh} disabled={loading} data-testid="critical-refresh">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {!loading && total === 0 && (
        <div className="vb-card p-6 my-4 text-center" data-testid="critical-all-clear">
          <CheckCircle2 size={40} className="mx-auto mb-2" style={{ color: "var(--vb-success, #4F8A3A)" }} />
          <h2 className="vb-heading text-lg m-0" style={{ color: "var(--vb-text-2)" }}>You're all caught up</h2>
          <p className="text-sm opacity-70 m-0 mt-1">No outstanding critical tasks right now. Nice work!</p>
        </div>
      )}

      <div className="space-y-3 mt-4">
        <TaskTile
          priority={1}
          icon={Trash2}
          tone="var(--vb-error)"
          title="Food nearing / past expiry"
          subtitle="Scan or review food items that must be disposed."
          count={foodExpiry}
          actions={[
            { label: "Dispose list", testid: "critical-food-dispose", onClick: () => nav("/app/food-prep?tab=disposal") },
            { label: "Scan item", ghost: true, icon: ScanLine, testid: "critical-food-scan", onClick: () => nav("/app/food-prep?tab=scan") },
          ]}
        />

        <TaskTile
          priority={2}
          icon={FileWarning}
          tone="#b45309"
          title="Non-compliant deviations"
          subtitle={isStaff ? "CCP deviations sent back to you for rework." : "CCP deviations awaiting rework, review or closure."}
          count={deviations}
          actions={[
            { label: isStaff ? "Rework tasks" : "Review deviations", testid: "critical-deviations-open", onClick: () => nav(devTarget) },
          ]}
        />

        <TaskTile
          priority={3}
          icon={Activity}
          tone="var(--vb-accent)"
          title="Daily task entry"
          subtitle="Log today's hygiene & cleanliness checks and CCP readings."
          count={dailyTasks}
          actions={[
            { label: "Daily tracking", testid: "critical-daily-open", onClick: () => nav("/app/daily-tracking") },
            { label: "Scan QR", ghost: true, icon: ScanLine, testid: "critical-scan-qr", onClick: () => setScanOpen(true) },
          ]}
        />

        <TaskTile
          priority={4}
          icon={Gauge}
          tone="#0f766e"
          title={isStaff ? "CCP readings to capture" : "CCP data capture & sign-off"}
          subtitle={isStaff
            ? "Equipment still missing today's scheduled temperature readings."
            : (ccpCapture || ccpSignoff)
              ? `${ccpCapture} equipment missing today's readings · ${ccpSignoff} day(s) awaiting your sign-off.`
              : "Chase missing readings and sign off completed days."}
          count={ccpTask}
          actions={[
            { label: isStaff ? "Capture readings" : "Review & sign off", testid: "critical-ccp-open", onClick: () => nav(ccpTarget) },
          ]}
        />
      </div>

      {scanOpen && <QRScannerModal onClose={() => setScanOpen(false)} />}
    </div>
  );
}
